<template>
  <div>
    <main class="min-h-screen bg-white pt-16 z-[1] relative mb-64">
      <!-- Hero Section -->
      <section class="border-b border-black px-4 py-8 sm:px-6 lg:px-8 pt-32 text-dark-teal-600">
        <div class="mx-auto max-w-screen-2xl">
          <div class="flex flex-col max-w-screen-sm gap-8 lg:gap-10 lg:max-w-screen-md">
          <div class="text-xl md:text-2xl">{{ t('pages.about.hero.kicker') }}</div>
          <h1 class="text-5xl font-medium md:text-6xl md:leading-[0.9] lg:text-8xl">{{ t('pages.about.hero.title') }}</h1>
          <div class="text-xl md:text-2xl">
            <em>{{ t('pages.about.hero.subtitle.highlight') }}</em> {{ t('pages.about.hero.subtitle.trailing') }}
          </div>
        </div>
          <div class="space-y-6 text-lg leading-relaxed max-w-3xl py-12">
            <p class="text-dark-teal-600">
              <i18n-t keypath="pages.about.story.paragraph1">
                <template #brand>
                  <span is="span" class="font-serif">ommi</span>
                </template>
              </i18n-t>
            </p>
            <p class="text-black">{{ t('pages.about.story.paragraph2') }}</p>
            <p class="text-black">
              <i18n-t keypath="pages.about.story.paragraph3">
                <template #brand>
                  <span is="span" class="font-serif">ommi</span>
                </template>
              </i18n-t>
            </p>
          </div>
        </div>
      </section>

      <!-- Process Section -->
      <section class="border-b border-black">
        <div class="grid grid-cols-2 md:grid-cols-4">
          <div class="relative aspect-square border-r-4 border-b md:border-b-0 border-black">
            <img 
              :alt="t('pages.about.process.steps.shaping.alt')" 
              loading="lazy" 
              class="absolute inset-0 w-full h-full object-cover" 
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/process-vase-making-ki9wWNGUsiFm6Sokue7FuQRFsKoMuX.jpg"
            >
            <div class="absolute bottom-4 left-4 bg-white px-3 py-1 border border-black">
              <span class="text-sm font-mono">{{ t('pages.about.process.steps.shaping.label') }}</span>
            </div>
          </div>
          <div class="relative aspect-square border-r-4 md:border-r-4 border-b md:border-b-0 border-black">
            <img 
              :alt="t('pages.about.process.steps.coating.alt')" 
              loading="lazy" 
              class="absolute inset-0 w-full h-full object-cover" 
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/process-vase-coating-e7wJE1qwkaXzXizShITWWhTVg8wZPg.jpg"
            >
            <div class="absolute bottom-4 left-4 bg-white px-3 py-1 border border-black">
              <span class="text-sm font-mono">{{ t('pages.about.process.steps.coating.label') }}</span>
            </div>
          </div>
          <div class="relative aspect-square border-r-4 border-black">
            <img 
              :alt="t('pages.about.process.steps.firing.alt')" 
              loading="lazy" 
              class="absolute inset-0 w-full h-full object-cover" 
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/process-vase-firing-YpSyL2fXUQE8zL3nLxhZIAzajlea1a.jpg"
            >
            <div class="absolute bottom-4 left-4 bg-white px-3 py-1 border border-black">
              <span class="text-sm font-mono">{{ t('pages.about.process.steps.firing.label') }}</span>
            </div>
          </div>
          <div class="relative aspect-square">
            <img 
              :alt="t('pages.about.process.steps.perfection.alt')" 
              loading="lazy" 
              class="absolute inset-0 w-full h-full object-cover" 
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/process-vase-imperfect-kDnFCxyGdKdndOfefFLX7PIM5gJX5O.jpg"
            >
            <div class="absolute bottom-4 left-4 bg-white px-3 py-1 border border-black">
              <span class="text-sm font-mono">{{ t('pages.about.process.steps.perfection.label') }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Team Section -->
      <section class="border-b border-black px-4 py-8 sm:px-6 lg:px-8 my-12">
        <div class="mx-auto max-w-screen-xl my-12">
          <h2 class="text-6xl text-black mb-4">{{ t('pages.about.team.title') }}</h2>
          <p class="text-xl text-black mb-12">{{ t('pages.about.team.subtitle') }}</p>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div class="group">
              <div class="relative aspect-square mb-4 border rounded border-black overflow-hidden">
                <img 
                  alt="Giorgia" 
                  loading="lazy" 
                  class="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-125" 
                  src="https://rural-pop-up.com/cdn-cgi/image/width=480,quality=75,format=webp,fit=scale-down//team/giorgia-gamba.webp"
                >
              </div>
              <h3 class="text-2xl text-black mb-1">Giorgia</h3>
              <p class="text-lg text-black/60">{{ t('pages.about.team.roles.giorgia') }}</p>
            </div>
            <div class="group">
              <div class="relative aspect-square mb-4 border rounded border-black overflow-hidden">
                <img 
                  alt="Tetiana" 
                  loading="lazy" 
                  class="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-125" 
                  src="/images/team/tetiana.jpeg"
                >
              </div>
              <h3 class="text-2xl text-black mb-1">Tetiana</h3>
              <p class="text-lg text-black/60">{{ t('pages.about.team.roles.tetiana') }}</p>
            </div>
            <div class="group">
              <div class="relative aspect-square mb-4 border rounded border-black overflow-hidden">
                <img 
                  alt="Hamza" 
                  loading="lazy" 
                  class="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-125" 
                  src="/images/team/hamza.jpeg"
                >
              </div>
              <h3 class="text-2xl text-black mb-1">Hamza</h3>
              <p class="text-lg text-black/60">{{ t('pages.about.team.roles.hamza') }}</p>
            </div>
            <div class="group">
              <div class="relative aspect-square mb-4 border rounded border-black overflow-hidden">
                <img 
                  alt="Enrico" 
                  loading="lazy" 
                  class="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-125" 
                  src="https://www.aboutme.it/enrico-icardi.webp"
                >
              </div>
              <h3 class="text-2xl text-black mb-1">Enrico</h3>
              <p class="text-lg text-black/60">{{ t('pages.about.team.roles.enrico') }}</p>
            </div>
            <div class="group">
              <div class="relative aspect-square mb-4 border rounded border-black overflow-hidden">
                <img 
                  alt="Greta" 
                  loading="lazy" 
                  class="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-125" 
                  src="https://performingdigital.com/assets/greta-pogliani-chi-siamo-performing-digital.png"
                >
              </div>
              <h3 class="text-2xl text-black mb-1">Greta</h3>
              <p class="text-lg text-black/60">{{ t('pages.about.team.roles.greta') }}</p>
            </div>
            <NuxtLink to="#" class="group border rounded border-black hover:bg-black hover:text-white transition-colors">
              <div class="aspect-square flex flex-col items-center justify-center">
                <span class="text-6xl mb-4 group-hover:text-white">+</span>
                <span class="text-2xl group-hover:text-white">{{ t('pages.about.team.cta.question') }}</span>
              </div>
              <div class="p-4 border-t-4 border-black">
                <p class="text-lg text-center group-hover:text-white">{{ t('pages.about.team.cta.title') }}</p>
              </div>
            </NuxtLink>
          </div>
        </div>
      </section>

      <!-- Mission Section -->
      <section class="px-4 py-8 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-screen-2xl">
          <h2 class="text-6xl text-black mb-12">{{ t('pages.about.mission.title') }}</h2>
          <p class="text-xl text-black leading-relaxed mb-16 max-w-3xl">{{ t('pages.about.mission.description') }}</p>
          <div class="grid md:grid-cols-3 gap-8">
            <div class="border rounded border-black p-8 hover:bg-black hover:text-white transition-colors group">
              <div class="text-4xl font-mono mb-6 group-hover:text-white">{{ t('pages.about.mission.cards.beauty.label') }}</div>
              <h3 class="text-2xl text-black mb-4 group-hover:text-white">{{ t('pages.about.mission.cards.beauty.title') }}</h3>
              <p class="text-base leading-relaxed text-black/80 group-hover:text-white/90">{{ t('pages.about.mission.cards.beauty.body') }}</p>
            </div>
            <div class="border rounded border-black p-8 hover:bg-black hover:text-white transition-colors group">
              <div class="text-4xl font-mono mb-6 group-hover:text-white">{{ t('pages.about.mission.cards.connection.label') }}</div>
              <h3 class="text-2xl text-black mb-4 group-hover:text-white">{{ t('pages.about.mission.cards.connection.title') }}</h3>
              <p class="text-base leading-relaxed text-black/80 group-hover:text-white/90">{{ t('pages.about.mission.cards.connection.body') }}</p>
            </div>
            <div class="border rounded border-black p-8 hover:bg-black hover:text-white transition-colors group">
              <div class="text-4xl font-mono mb-6 group-hover:text-white">{{ t('pages.about.mission.cards.growth.label') }}</div>
              <h3 class="text-2xl text-black mb-4 group-hover:text-white">{{ t('pages.about.mission.cards.growth.title') }}</h3>
              <p class="text-base leading-relaxed text-black/80 group-hover:text-white/90">{{ t('pages.about.mission.cards.growth.body') }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '#imports'
import { useSeo } from '~/composables/useSeo'

const { t } = useI18n()

definePageMeta({
  layout: 'default'
})

useSeo({
  title: t('pages.about.meta.title') || 'About - ommi.shop',
  description: t('pages.about.meta.description') || 'Learn about ommi.shop, the marketplace for imperfect art and unique handmade pieces. Discover our mission, team, and the story behind celebrating imperfection.',
  keywords: 'ommi.shop, about, handmade, imperfect art, artisan marketplace, unique crafts, team',
  type: 'website'
})
</script> 